import { Injectable, WritableSignal, signal } from '@angular/core';
import { RouteFormOptionsService } from '../components/route-form-options-panels/services/route-form-options.service';
import { SelectRoutePlacesService } from '../components/select-route-form/services/select-route-places.service';
import { OtpService } from '@api/index';
import { Observable, combineLatest, map, of, switchMap, tap } from 'rxjs';
import { Plan, Itinerary, RouteRequestResult, RouteFormResultPreManagement, RouteRequestParams } from '../models/routeRequestResults.model';
import moment from 'moment';
import { UtilsService } from '@shared/services/utils.service';
import { RouteFormMoreOptionsService } from '../components/route-form-options-panels/components/route-form-more-options/services/route-form-more-options.service';
import { MoreOptionsRequestParameters, otpRouteRequestParameters } from '@api/models/otpRouteRequest.model';

@Injectable({
  providedIn: 'root'
})
export class RouteFormService {

  public lastRouteRequestParams:WritableSignal<RouteRequestParams|null> = signal(null);


  constructor(private routeFormOptionsService: RouteFormOptionsService, private selectRoutePlacesService: SelectRoutePlacesService,
              private otpService: OtpService, private utils: UtilsService, private moreOptionsService: RouteFormMoreOptionsService) { }

  private getRouteDataForRequest() {
    return {
      ...this.routeFormOptionsService.getOptionsForRequest(),
      ...this.selectRoutePlacesService.getPlacesForRequest()
    }
  }

  public setFormsWithUrlParams(params: RouteRequestParams) {
    this.routeFormOptionsService.setOptionsFromUrlParams(params);
  }

  public getLastRouteRequestParams() {
    const params = {...this.lastRouteRequestParams()};
    return params;
  }


  public doSearchRequest(params?: RouteRequestParams){
    const data = params ? params : this.obtainRequestParams();
    if (!data) { 
      this.lastRouteRequestParams.set(null);
      return of(null) 
    }
    const dateOptions = {
      fromPlace: data.fromPlace,
      toPlace: data.toPlace,
      arriveBy: data.arriveBy,
      date: data.date,
      time: data.time,
    }
    const moreOptionsData = {
      bus: data.bus,
      walk: data.walk,
      bicycle: data.bicycle,
      all: data.all,
    }
    return this.getRequestsObservable(dateOptions, moreOptionsData).pipe(
      map((result) => {
        if ((result.bus.error || result.bus.plan.itineraries.length === 0) && (result.walk.error || result.walk.plan.itineraries.length === 0) && 
            (result.bicycle.error || result.bicycle.plan.itineraries.length === 0) && (result.all.error || result.all.plan.itineraries.length === 0)){
          this.utils.showSnackBarError('notRoutesFound');
          return null;
        }
        this.lastRouteRequestParams.set(data);
        return this.orderItinerariesResult(data, result);
    }))
  }

  private obtainRequestParams(params?: RouteRequestParams): RouteRequestParams | null {
    const formData = params || this.getRouteDataForRequest();
    if(!formData.fromPlace || !formData.toPlace){ return null } 
    return {
      ...formData,
      fromPlace: `${formData.fromPlace[1]},${formData.fromPlace[0]}`,
      toPlace: `${formData.toPlace[1]},${formData.toPlace[0]}`,
    }
  }

  private getRequestsObservable(data: otpRouteRequestParameters, moreOptionsData: MoreOptionsRequestParameters) {
    return combineLatest({
      bus: this.otpService.routeRequest(data, moreOptionsData.bus,'TRANSIT').pipe(
        switchMap((busResult) => {
          if (busResult.error || busResult.plan.itineraries.length <= 1) return of(busResult);
          const busResultWithoutOnlyWalkItineraries = busResult.plan.itineraries.filter((itinerary) => itinerary.legs.some((leg) => leg.mode !== 'WALK'))
          return of({ ...busResult, plan: { ...busResult.plan, itineraries: busResultWithoutOnlyWalkItineraries } });
        }) 
      ),
      walk: this.otpService.routeRequest(data, moreOptionsData.walk,'WALK'),
      bicycle: this.otpService.routeRequest(data, moreOptionsData.bicycle,'BICYCLE'),
      all: this.getAllOptionsObservable(data, moreOptionsData)
    })
  }

  private getAllOptionsObservable(data: otpRouteRequestParameters, moreOptionsData: MoreOptionsRequestParameters) {
    return this.otpService.routeRequest(data, moreOptionsData.all,'TRANSIT, WALK').pipe(
      switchMap((allResult) => {
        if (allResult.error) {
          this.lastRouteRequestParams.set(null);
          return of(allResult)
        }
        const observablesArray:Observable<any>[] = [];
        observablesArray.push(...this.modifyAllOptionsRouteToBicycle(allResult, data, moreOptionsData));
        if(observablesArray.length === 0) return of(allResult);
        return combineLatest(observablesArray).pipe(
        switchMap((result) => {
          return this.calculateTimesOfAllOptionsRoute(allResult, result)
        })
        );
      }),
    )
  }

  private modifyAllOptionsRouteToBicycle(allResult: RouteRequestResult, data:otpRouteRequestParameters, moreOptionsData: MoreOptionsRequestParameters) {
    const newRequests:Observable<any>[] = [];
    allResult.plan.itineraries.forEach((itinerary) => {
      itinerary.legs.forEach((leg) => {
        if(leg.mode === 'BUS') newRequests.push(of(leg))
        else {
          const newDataRequest = {
            ...data,
            time: moment(leg.startTime).format('h:mm A'),
            fromPlace: `${leg.from.lat},${leg.from.lon}`,
            toPlace: `${leg.to.lat},${leg.to.lon}`,
          }
          newRequests.push(this.otpService.routeRequest(newDataRequest, moreOptionsData.all,'BICYCLE'))
        }
      });
    })
    return newRequests;
  }

  private calculateTimesOfAllOptionsRoute(allResult: RouteRequestResult, allModifiedRequestsResult: any[]) {
    let globalIndex = 0;
    allResult.plan.itineraries.forEach((itinerary) => {
      itinerary.startTime = allModifiedRequestsResult[globalIndex].requestParameters ?  allModifiedRequestsResult[globalIndex].plan.itineraries[0].startTime : allModifiedRequestsResult[globalIndex].startTime;
      itinerary.legs.forEach((leg, legIndex) => {
        itinerary.legs[legIndex] = allModifiedRequestsResult[globalIndex].requestParameters ?  allModifiedRequestsResult[globalIndex].plan.itineraries[0].legs[0] : allModifiedRequestsResult[globalIndex];
        globalIndex++;
        if(legIndex === itinerary.legs.length - 1) {
          itinerary.endTime = leg.endTime;
        }
      });
      itinerary.endTime = itinerary.legs[itinerary.legs.length -1].endTime;
      itinerary.duration = moment(itinerary.endTime).diff(moment(itinerary.startTime),'seconds');
    })
    return of(allResult)
  }

  private orderItinerariesResult(data: RouteRequestParams, result: RouteFormResultPreManagement) {
        //Order itineraries by date
        const walkPlanItinerariesOrdered = result.walk.plan.itineraries.sort((a,b) => a.startTime - b.startTime)
        const walkPlan:Plan = { ...result.walk.plan, itineraries: walkPlanItinerariesOrdered }
        const walkPlanMinDuration = walkPlan.itineraries.length > 0 ? Math.ceil(this.obtainItineraryWithLessDuration(walkPlan.itineraries).duration) : -1
        const busPlanItinerariesOrdered = result.bus.plan.itineraries.sort((a,b) => a.startTime - b.startTime)
        const busPlan:Plan = { ...result.bus.plan, itineraries: busPlanItinerariesOrdered }
        const busPlanMinDuration = busPlan.itineraries.length > 0 ? Math.ceil(this.obtainItineraryWithLessDuration(busPlan.itineraries).duration) : -1
        const bicyclePlanItinerariesOrdered = result.bicycle.plan.itineraries.sort((a,b) => a.startTime - b.startTime)
        const bicyclePlan:Plan = { ...result.bicycle.plan, itineraries: bicyclePlanItinerariesOrdered }
        const bicyclePlanMinDuration = bicyclePlan.itineraries.length > 0 ? Math.ceil(this.obtainItineraryWithLessDuration(bicyclePlan.itineraries).duration) : -1
        const allPlanItinerariesOrdered = result.all.plan.itineraries.sort((a,b) => a.startTime - b.startTime)
        const allPlan:Plan = { ...result.all.plan, itineraries: allPlanItinerariesOrdered}
        const allPlanMinDuration = allPlan.itineraries.length > 0 ? Math.ceil(this.obtainItineraryWithLessDuration(allPlan.itineraries).duration) : -1
        
        return {
          walk: { ...result.walk, plan: {...walkPlan, itineraries: walkPlan.itineraries.map(itinerary => this.addDistanceToItinerary(itinerary)), minDuration: walkPlanMinDuration === 0 ? 1 : walkPlanMinDuration } },
          bus: { ...result.bus, plan: {...busPlan, itineraries: busPlan.itineraries.map(itinerary => this.addDistanceToItinerary(itinerary)), minDuration: busPlanMinDuration === 0 ? 1 : busPlanMinDuration } },
          bicycle: { ...result.bicycle, plan: {...bicyclePlan, itineraries: bicyclePlan.itineraries.map(itinerary => this.addDistanceToItinerary(itinerary)), minDuration: bicyclePlanMinDuration === 0 ? 1 : bicyclePlanMinDuration } },
          all: { ...result.all, plan: {...allPlan, itineraries: allPlan.itineraries.map(itinerary => this.addDistanceToItinerary(itinerary)), minDuration: allPlanMinDuration === 0 ? 1 : allPlanMinDuration } },
          requestParameters: {
            ...data,
            date: moment(data.date,'MM/DD/YYYY').format('DD-MM-YYYY'),
          }
        }
  
  }
  
  private obtainItineraryWithLessDuration(itineraries: Itinerary[]) {
    return itineraries.reduce((acc, itinerary) => {
      if(itinerary.duration < acc.duration) return itinerary;
      return acc;
    }, itineraries[0])
  }

  private addDistanceToItinerary(itinerary: Itinerary) {
    return {
      ...itinerary,
      distance: itinerary.legs.reduce((acc, leg) => acc + leg.distance, 0)
    }
  }

}
